import { Badge, type BadgeColor } from '@civilgrid/ui/components';
import { updateDrfRequest as apiUpdateRequest } from '~/api/drf';
import { drfStatusLabels } from '~/common/drfUtils';
import { DrfRequestStatus, type MapFulfillmentRequest } from '~/common/types';
import { dateTimeSortingFn, type ExportableColumnDef } from '~/components2/Table';
import FreeFormTextCell from '~/components2/Table/Cells/FreeFormTextCell';

const gv = (value: any, defaultValue: any = '') => {
  return value || defaultValue;
};

const drfStatusColors: Record<DrfRequestStatus, BadgeColor> = {
  [DrfRequestStatus.Fulfilled]: 'green',
  [DrfRequestStatus.AutoFulfilled]: 'green',
  [DrfRequestStatus.Pending]: 'yellow',
  [DrfRequestStatus.PaymentPending]: 'blue',
  [DrfRequestStatus.NeedsReview]: 'orange',
  [DrfRequestStatus.Rejected]: 'red',
  [DrfRequestStatus.Cancelled]: 'grey',
};

export const columns: ExportableColumnDef<MapFulfillmentRequest>[] = [
  {
    id: 'requestId',
    size: 150,
    header: 'Request ID',
    accessorKey: 'requestId',
  },
  {
    id: 'externalRefId',
    size: 175,
    header: 'External Ref ID',
    accessorFn: (r) => gv(r.externalRefId),
  },
  {
    id: 'sharedLinkUrl',
    header: 'Shared Link URL',
    accessorFn: (r) => gv(r.sharedLinkUrl),
    cell: ({ getValue }) => {
      const link = getValue<string>();
      return (
        <div className="relative h-full w-full overflow-hidden">
          {link ? (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="block h-full w-full overflow-hidden text-ellipsis whitespace-nowrap underline"
            >
              {link}
            </a>
          ) : (
            ''
          )}
        </div>
      );
    },
  },
  {
    id: 'requestStatus',
    header: 'Request Status',
    accessorFn: (r) => drfStatusLabels[r.requestStatus] ?? '',
    filterFn: (row, _, filterValue) => {
      if (!filterValue) return true;

      const status = row.original.requestStatus;
      const columnValue = drfStatusLabels[status];

      if (filterValue === drfStatusLabels[DrfRequestStatus.Pending]) {
        return status === DrfRequestStatus.Pending || status === DrfRequestStatus.NeedsReview;
      }

      return columnValue === filterValue;
    },
    cell: (cell) => {
      const status = cell.row.original.requestStatus;
      const label = drfStatusLabels[status];

      return <Badge color={drfStatusColors[status]}>{label}</Badge>;
    },
  },
  {
    id: 'name',
    header: 'Name',
    accessorFn: (r) => gv(r.name),
  },
  {
    id: 'notes',
    header: 'Internal Notes',
    accessorFn: (r) => gv(r.notes),
    cell: (cell) => {
      const requestId = cell.row.id;
      const rowIndex = cell.row.index;
      const columnId = cell.column.id;
      const value = cell.getValue();

      async function onCommit(notes: string) {
        const res = await apiUpdateRequest(requestId, { notes });
        if (!res.id) throw new Error('Bad response!');
        cell.table.options.meta?.updateRow?.(rowIndex, columnId, notes);
      }

      return <FreeFormTextCell initialValue={value} onCommit={onCommit} />;
    },
  },
  {
    id: 'client',
    header: 'Client',
    accessorFn: (r) => gv(r.client),
  },
  {
    id: 'scope',
    header: 'Scope',
    accessorFn: (r) => gv(r.scope),
  },
  {
    id: 'startDate',
    header: 'Start Date',
    accessorFn: (r) => gv(r.startDate?.toLocaleString()),
    sortingFn: (a, b) => dateTimeSortingFn(a, b, 'startDate'),
  },
  {
    id: 'endDate',
    header: 'End Date',
    accessorFn: (r) => gv(r.endDate?.toLocaleString()),
    sortingFn: (a, b) => dateTimeSortingFn(a, b, 'endDate'),
  },
  {
    id: 'isPublicProject',
    header: 'Public Project',
    accessorFn: (r) => r.isPublicProject ?? false,
    size: 65,
    cell: ({ getValue }) => {
      return <div className="flex h-full w-full items-center justify-center">{getValue() ? '✅' : ''}</div>;
    },
  },
  {
    id: 'requestor',
    header: 'Requestor',
    columns: [
      {
        id: 'requestor.name',
        header: 'Name',
        accessorFn: (r) => gv(r.requestor?.name),
      },
      {
        id: 'requestor.email',
        header: 'Email',
        accessorFn: (r) => gv(r.requestor?.email),
      },
      {
        id: 'requestor.phone',
        header: 'Phone',
        accessorFn: (r) => gv(r.requestor?.phone),
      },
      {
        id: 'requestor.organization',
        header: 'Organization',
        accessorFn: (r) => gv(r.requestor?.organization),
      },
      {
        id: 'requestor.address',
        header: 'Address',
        columns: [
          {
            id: 'requestor.address.street',
            header: 'Street',
            accessorFn: (r) => gv(r.requestor?.address?.street),
          },
          {
            id: 'requestor.address.city',
            header: 'City',
            accessorFn: (r) => gv(r.requestor?.address?.city),
          },
          {
            id: 'requestor.address.state',
            header: 'State',
            accessorFn: (r) => gv(r.requestor?.address?.state),
          },
          {
            id: 'requestor.address.zipCode',
            header: 'ZIP Code',
            accessorFn: (r) => gv(r.requestor?.address?.zipCode),
          },
        ],
      },
    ],
  },
  {
    id: 'geometryType',
    header: 'Geometry Type',
    accessorKey: 'geometryType',
    cell: ({ getValue }) => <div className="capitalize">{getValue()}</div>,
  },
  {
    id: 'areaInAcres',
    header: 'Area (ac.)',
    accessorFn: (r) => gv(r.areaInAcres?.toFixed(2)),
  },
  {
    id: 'length',
    header: 'Length (mi.)',
    accessorFn: (r) => gv(r.linear?.lengthInMiles?.toFixed(2), 'N/A'),
  },
  {
    id: 'completedBy',
    header: 'Completed By',
    accessorFn: (r) => gv(r.completedBy?.name),
  },
  {
    id: 'completedAt',
    header: 'Completed At',
    accessorFn: (r) => gv(r.completedAt?.toLocaleString()),
    sortingFn: (a, b) => dateTimeSortingFn(a, b, 'completedAt'),
  },
  {
    id: 'createdAt',
    header: 'Created At',
    accessorFn: (r) => gv(r.createdAt?.toLocaleString()),
    sortingFn: (a, b) => dateTimeSortingFn(a, b, 'createdAt'),
  },
  {
    id: 'updatedAt',
    header: 'Updated At',
    accessorFn: (r) => gv(r.updatedAt?.toLocaleString()),
    sortingFn: (a, b) => dateTimeSortingFn(a, b, 'updatedAt'),
  },
];
